import { Form, FormInstance } from "antd";
import { DefaultOptionType } from "antd/es/select";
import to from "await-to-js";
import { useEffect, useRef, useState } from "react";
import { exciseStampsService } from "../../modules/obligee/exciseStamps/exciseStamps.service";
import { AkciznaMarkicaKategorijaProzivoda } from "../../modules/obligee/exciseStamps/exciseStamps.types";

type CacheEntry = {
  opsegKategorije: Array<DefaultOptionType> | null;
  markiceAndAmbalaze: Array<{
    markicaId: number | null;
    showAmbalaza: boolean;
  }> | null;
};

export const useMarkicaZahtevIsporuceniOpsezi = ({
  form,
  zahtevFieldName = "brojZahteva",
  kategorijaFieldName = "vrstaKategorijeId",
  ambalazaFieldName,
  markicaKategorijeId,
  listFieldName,
  onKategorijaChange,
}: {
  form: FormInstance;
  zahtevFieldName?: string | (string | number)[];
  kategorijaFieldName?: string | (string | number)[];
  ambalazaFieldName?: string | (string | number)[];
  markicaKategorijeId: AkciznaMarkicaKategorijaProzivoda | null;
  listFieldName?: string;
  onKategorijaChange?: () => void;
}) => {
  const [showAmbalaza, setShowAmbalaza] = useState<boolean | null>(null);
  const [isLoadingOpsegKategorije, setIsLoadingOpsegKategorije] =
    useState(false);
  const [isOpsegKategorijeDisabled, setIsOpsegKategorijeDisabled] =
    useState(true);
  const [opsegKategorije, setOpsegKategorije] = useState<
    Array<DefaultOptionType>
  >([]);
  const [markiceAndAmbalaze, setMarkiceAndAmbalaze] = useState<
    Array<{
      markicaId: number | null;
      showAmbalaza: boolean;
    }>
  >([]);
  const [opsezi, setOpsezi] = useState<Array<DefaultOptionType>>([]);

  const brojZahtevaValue = Form.useWatch(
    Array.isArray(zahtevFieldName)
      ? [listFieldName, ...zahtevFieldName]
      : zahtevFieldName,
    form
  );

  const [error, setError] = useState<{
    message: string | null;
    brojZahteva: string;
  } | null>(null);

  // Only enable caching when used inside a Form.List
  const cacheRef = useRef<Map<string, CacheEntry> | null>(
    listFieldName ? new Map() : null
  );

  // Restore cached values (needed when indexes are changed - group rerenders)
  useEffect(() => {
    // Only restore from cache if caching is enabled (inside a list)
    if (
      !listFieldName ||
      !cacheRef.current ||
      opsegKategorije.length > 0 ||
      !brojZahtevaValue
    ) {
      return;
    }

    const cached = cacheRef.current.get(
      `${brojZahtevaValue}_${markicaKategorijeId}`
    );

    if (cached) {
      let opsegKategorijeCached = Array.isArray(cached.opsegKategorije)
        ? cached.opsegKategorije
        : [];
      let markiceAndAmbalazeCached = Array.isArray(cached.markiceAndAmbalaze)
        ? cached.markiceAndAmbalaze
        : [];

      setOpsegKategorije(opsegKategorijeCached);
      setMarkiceAndAmbalaze(markiceAndAmbalazeCached);
      setIsOpsegKategorijeDisabled(false);

      const selectedKategorijaId = form.getFieldValue(kategorijaFieldName) as
        | number
        | null;

      if (selectedKategorijaId) {
        const selectedKategorija = opsegKategorijeCached?.find(
          (k) => k.value === selectedKategorijaId
        );

        if (selectedKategorija && Array.isArray(selectedKategorija.children)) {
          setOpsezi(
            selectedKategorija.children.map((opseg: any) => ({
              value: opseg.id,
              label: opseg.opseg,
            }))
          );
        }

        const markicaHasAmbalaza = markiceAndAmbalazeCached?.find(
          (element) => element.markicaId === selectedKategorijaId
        )?.showAmbalaza;

        if (markicaHasAmbalaza) {
          setShowAmbalaza(markicaHasAmbalaza);
        } else {
          setShowAmbalaza(false);
        }
      }
    }
  }, [
    listFieldName,
    markicaKategorijeId,
    opsegKategorije.length,
    form,
    kategorijaFieldName,
  ]);

  const onBrojZahtevaSubmitSettled = () => {
    form.validateFields([zahtevFieldName]);
  };

  const handleBrojZahtevaOpsegChange = async (brojZahteva: string) => {
    form.setFieldValue(zahtevFieldName, brojZahteva);

    if (!brojZahteva) {
      return;
    }

    onKategorijaChange?.();

    setIsLoadingOpsegKategorije(true);

    const [err, res] = await to(
      exciseStampsService.getMarkicaZahtevIsporuceniOpsezi(
        brojZahteva,
        markicaKategorijeId
      )
    );

    if (err || !res) {
      setIsLoadingOpsegKategorije(false);
      setIsOpsegKategorijeDisabled(true);
      setOpsegKategorije([]);
      setTimeout(() => {
        onBrojZahtevaSubmitSettled();
      });
      return;
    }

    if (!res.success && res.errorMessage && !res.data) {
      setError({ message: res.errorMessage, brojZahteva });
      setIsLoadingOpsegKategorije(false);
      setTimeout(() => {
        onBrojZahtevaSubmitSettled();
      });
      return;
    }

    if (res.data) {
      setError(null);

      const mappedOpsegKategorije = res.data?.map((element) => ({
        label: element.markicaKategorijeVrsteIme,
        value: element.markicaKategorijaVrsteId!,
        children: element.opsezi,
      }));

      const mappedMarkiceAndAmbalaze = res.data?.map((element) => ({
        markicaId: element.markicaKategorijaVrsteId,
        showAmbalaza: element.showAmbalaza,
      }));

      // Only cache if used inside a list
      if (listFieldName && cacheRef.current) {
        const cacheKey = `${brojZahteva}_${markicaKategorijeId}`;
        cacheRef.current.set(cacheKey, {
          opsegKategorije: mappedOpsegKategorije,
          markiceAndAmbalaze: mappedMarkiceAndAmbalaze,
        });
      }

      setOpsegKategorije(mappedOpsegKategorije);
      setMarkiceAndAmbalaze(mappedMarkiceAndAmbalaze);

      if (res.data?.length === 1) {
        form.setFieldValue(
          kategorijaFieldName,
          res.data?.[0]?.markicaKategorijaVrsteId
        );

        setOpsezi(
          res.data?.[0]?.opsezi?.map((opseg) => ({
            value: opseg.id,
            label: opseg.opseg,
          }))
        );
        await form.validateFields([kategorijaFieldName]);
      }

      setIsLoadingOpsegKategorije(false);
      setIsOpsegKategorijeDisabled(false);
      setTimeout(() => {
        onBrojZahtevaSubmitSettled();
      });
    }
  };

  const resetOpsegKategorije = () => {
    setOpsegKategorije([]);
    setOpsezi([]);
  };

  const handleVrstaKategorijeOpsegChange = (
    value: number,
    option: DefaultOptionType | DefaultOptionType[]
  ) => {
    if (Array.isArray(option)) {
      return;
    }

    form.setFieldValue(kategorijaFieldName, value);

    const markicaHasAmbalaza = markiceAndAmbalaze.find(
      (element) => element.markicaId === value
    )?.showAmbalaza;

    if (markicaHasAmbalaza) {
      setShowAmbalaza(markicaHasAmbalaza);
    } else {
      if (
        listFieldName &&
        Array.isArray(ambalazaFieldName) &&
        ambalazaFieldName?.[1]
      ) {
        form.setFieldValue([listFieldName, ...ambalazaFieldName], null);
      }

      setShowAmbalaza(false);
    }

    if (Array.isArray(option.children)) {
      setOpsezi(
        option?.children?.map((opseg) => ({
          value: opseg.id,
          label: opseg.opseg,
        }))
      );
    }

    onKategorijaChange?.();
  };

  return {
    isLoadingOpsegKategorije,
    isOpsegKategorijeDisabled,
    opsegKategorije,
    opsezi,
    brojZahtevaOpsegError: error,
    handleBrojZahtevaOpsegChange,
    resetOpsegKategorije,
    handleVrstaKategorijeOpsegChange,
    showAmbalaza,
    setShowAmbalaza,
  };
};
